"use client";

import { c as _c } from "react/compiler-runtime";
import { CaretLeft, CaretRight, Check, X } from "@phosphor-icons/react";
import { useLocale } from "next-intl";
import { useRef, useState } from "react";
import { useListingTransition } from "@/components/storefront/ListingTransitionContext";
import { Drawer } from "@/components/storefront/ui/drawer";
import { OverlayCloseButton } from "@/components/storefront/ui/overlay-close-button";
import { buildCategoryNav, type CategoryChipGroup, type CategoryNavTile, toggleSlug, withTreeSelection } from "@/lib/storefront/products/categoryNav";
import type { FacetCategoryDTO } from "@/lib/storefront/products/listService";
import { SafeImage } from "./SafeImage";

/**
 * All-products browsing: breadcrumb, a swipeable row of round category tiles
 * (single-select, drills into children) and a chip bar for the `chips` groups.
 * Everything writes `?category=` and lets the server re-render.
 */
export default function CategoryNav(t0) {
  const $ = _c(20);
  const {
    categories,
    appliedSlugs,
    treeSlugs
  } = t0;
  const isRTL = useLocale() === "ar";
  const {
    navigateWithParams
  } = useListingTransition();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== appliedSlugs || $[1] !== categories || $[2] !== isRTL || $[3] !== navigateWithParams || $[4] !== treeSlugs) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const nav = buildCategoryNav(categories, appliedSlugs, treeSlugs);
      const chipGroups = nav.chipGroups.filter(_temp);
      if (nav.tiles.length === 0 && chipGroups.length === 0) {
        t5 = null;
        break bb0;
      }
      let t6;
      if ($[10] !== navigateWithParams) {
        t6 = slugs => navigateWithParams(p => {
          if (slugs.length === 0) {
            p.delete("category");
          } else {
            p.set("category", slugs.join(","));
          }
          p.delete("page");
        });
        $[10] = navigateWithParams;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      const setCategories = t6;
      const selectTree = slug => setCategories(withTreeSelection(appliedSlugs, nav, slug));
      let t7;
      if ($[12] !== appliedSlugs || $[13] !== setCategories) {
        t7 = slug_0 => setCategories(toggleSlug(appliedSlugs, slug_0));
        $[12] = appliedSlugs;
        $[13] = setCategories;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      const toggle = t7;
      t1 = "mt-4";
      t2 = nav.trail.length > 0 && <nav aria-label={isRTL ? "\u0645\u0633\u0627\u0631 \u0627\u0644\u0623\u0635\u0646\u0627\u0641" : "Category path"} className="mb-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"><button type="button" onClick={() => selectTree(null)} className="hover:text-foreground transition-colors">{isRTL ? "\u0643\u0644 \u0627\u0644\u0623\u0635\u0646\u0627\u0641" : "All types"}</button>{nav.trail.map((item, i) => <span key={item.slug} className="inline-flex items-center gap-1.5"><span aria-hidden="true">›</span>{i === nav.trail.length - 1 ? <span className="font-medium text-foreground">{item.name}</span> : <button type="button" onClick={() => selectTree(item.slug)} className="hover:text-foreground transition-colors">{item.name}</button>}</span>)}</nav>;
      t3 = (nav.tiles.length > 0 || nav.allTile) && <TileRow tiles={nav.tiles} allTile={nav.allTile} isRTL={isRTL} onSelect={selectTree} />;
      t4 = chipGroups.length > 0 && <div className="mt-3 -mx-4 px-4 sm:mx-0 sm:px-0 py-2 bg-background overflow-x-auto scrollbar-hide"><div className="flex items-center gap-2 min-w-max">{chipGroups.map(group => group.mode === "toggles" ? group.options.map(o => <Chip key={o.slug} selected={o.selected} onClick={() => toggle(o.slug)}>{o.name}</Chip>) : <SheetChip key={group.rootSlug} group={group} isRTL={isRTL} onToggle={toggle} />)}</div></div>;
    }
    $[0] = appliedSlugs;
    $[1] = categories;
    $[2] = isRTL;
    $[3] = navigateWithParams;
    $[4] = treeSlugs;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t6 = <div className={t1}>{t2}{t3}{t4}</div>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}

// ─── Tile row ──────────────────────────────────────────────────────
function _temp(g) {
  return g.options.length > 0;
}
function TileRow(t0) {
  const $ = _c(34);
  const {
    tiles,
    allTile,
    isRTL,
    onSelect
  } = t0;
  const scroller = useRef(null);
  let t1;
  if ($[0] !== isRTL) {
    t1 = towardStart => scroller.current?.scrollBy?.({
      left: (towardStart !== isRTL ? -1 : 1) * 240,
      behavior: "smooth"
    });
    $[0] = isRTL;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scrollRow = t1;
  const StartCaret = isRTL ? CaretRight : CaretLeft;
  const EndCaret = isRTL ? CaretLeft : CaretRight;
  let t2;
  if ($[2] !== scrollRow) {
    t2 = () => scrollRow(true);
    $[2] = scrollRow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isRTL ? "\u0627\u0644\u0633\u0627\u0628\u0642" : "Previous";
  let t4;
  if ($[4] !== StartCaret) {
    t4 = <StartCaret size={16} />;
    $[4] = StartCaret;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <button type="button" onClick={t2} aria-label={t3} className="hidden md:flex shrink-0 h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-foreground hover:bg-muted transition-colors">{t4}</button>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== allTile || $[11] !== isRTL || $[12] !== onSelect) {
    t6 = allTile && <li className="snap-start shrink-0"><TileButton name={allTile.name} label={`${isRTL ? "\u0627\u0644\u0643\u0644" : "All"} ${allTile.name}`} selected={allTile.selected} onClick={() => onSelect(allTile.slug)} circle={<span className="absolute inset-0 flex items-center justify-center bg-foreground text-background text-xs font-semibold">{isRTL ? "\u0627\u0644\u0643\u0644" : "All"}</span>} /></li>;
    $[10] = allTile;
    $[11] = isRTL;
    $[12] = onSelect;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onSelect || $[15] !== tiles) {
    let t8;
    if ($[17] !== onSelect) {
      t8 = tile => <li key={tile.slug} className="snap-start shrink-0"><TileButton name={tile.name} selected={tile.selected} onClick={() => onSelect(tile.slug)} circle={<SafeImage src={tile.image_url} alt="" fill={true} className="object-cover" sizes="60px" blurDataURL={tile.blur_data_url} fallback={<span className="absolute inset-0 flex items-center justify-center bg-muted text-sm font-semibold text-muted-foreground">{tile.name.slice(0, 1)}</span>} />} /></li>;
      $[17] = onSelect;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = tiles.map(t8);
    $[14] = onSelect;
    $[15] = tiles;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[19] !== t6 || $[20] !== t7) {
    t8 = <ul ref={scroller} className="flex-1 min-w-0 -mx-4 px-4 md:mx-0 md:px-0 flex gap-3 overflow-x-auto snap-x scrollbar-hide">{t6}{t7}</ul>;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== scrollRow) {
    t9 = () => scrollRow(false);
    $[22] = scrollRow;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = isRTL ? "\u0627\u0644\u062A\u0627\u0644\u064A" : "Next";
  let t11;
  if ($[24] !== EndCaret) {
    t11 = <EndCaret size={16} />;
    $[24] = EndCaret;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t9) {
    t12 = <button type="button" onClick={t9} aria-label={t10} className="hidden md:flex shrink-0 h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-foreground hover:bg-muted transition-colors">{t11}</button>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== t5 || $[32] !== t8) {
    t13 = <div className="flex items-center gap-2">{t5}{t8}{t12}</div>;
    $[30] = t12;
    $[31] = t5;
    $[32] = t8;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function TileButton(t0) {
  const $ = _c(12);
  const {
    name,
    label,
    selected,
    onClick,
    circle
  } = t0;
  const t1 = `relative block h-[60px] w-[60px] overflow-hidden rounded-full bg-muted ${selected ? "ring-2 ring-foreground ring-offset-2 ring-offset-background" : ""}`;
  let t2;
  if ($[0] !== circle || $[1] !== t1) {
    t2 = <span aria-hidden="true" className={t1}>{circle}</span>;
    $[0] = circle;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `w-full text-center text-[11px] leading-4 line-clamp-2 ${selected ? "font-semibold text-foreground" : "text-foreground/80"}`;
  let t4;
  if ($[3] !== name || $[4] !== t3) {
    t4 = <span className={t3}>{name}</span>;
    $[3] = name;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== label || $[7] !== onClick || $[8] !== selected || $[9] !== t2 || $[10] !== t4) {
    t5 = <button type="button" onClick={onClick} aria-pressed={selected} aria-label={label} className="group flex w-[72px] flex-col items-center gap-1.5 py-1">{t2}{t4}</button>;
    $[6] = label;
    $[7] = onClick;
    $[8] = selected;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// ─── Chips ─────────────────────────────────────────────────────────

const CHIP_BASE = "inline-flex h-8 shrink-0 items-center gap-1 rounded-full border px-3 text-xs font-medium transition-colors";
const chipTone = (selected: boolean) => selected ? "border-foreground bg-foreground text-background" : "border-border bg-background text-foreground hover:border-foreground/40";
function Chip(t0) {
  const $ = _c(9);
  const {
    selected,
    onClick,
    opensDialog,
    children
  } = t0;
  const isTrigger = opensDialog !== undefined;
  const t1 = isTrigger ? undefined : selected;
  const t2 = isTrigger ? "dialog" : undefined;
  let t3;
  if ($[0] !== selected) {
    t3 = chipTone(selected);
    $[0] = selected;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `${CHIP_BASE} ${t3}`;
  let t5;
  if ($[2] !== children || $[3] !== onClick || $[4] !== opensDialog || $[5] !== t1 || $[6] !== t2 || $[7] !== t4) {
    t5 = <button type="button" onClick={onClick} aria-pressed={t1} aria-haspopup={t2} aria-expanded={opensDialog} className={t4}>{children}</button>;
    $[2] = children;
    $[3] = onClick;
    $[4] = opensDialog;
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function SheetChip(t0) {
  const $ = _c(29);
  const {
    group,
    isRTL,
    onToggle
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== group.options) {
    t1 = group.options.filter(_temp2);
    $[0] = group.options;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selected = t1;
  const single = selected.length === 1 ? selected[0] : null;
  const label = single ? single.name : selected.length > 1 ? `${group.name} (${selected.length})` : group.name;
  let t2;
  if ($[2] !== isRTL || $[3] !== label || $[4] !== onToggle || $[5] !== open || $[6] !== selected.length || $[7] !== single) {
    t2 = single ? <span className={`${CHIP_BASE} ${chipTone(true)} pe-1`}><button type="button" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open}>{label}</button><button type="button" onClick={() => onToggle(single.slug)} aria-label={`${isRTL ? "\u0625\u0632\u0627\u0644\u0629" : "Remove"} ${single.name}`} className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-background/20"><X size={12} weight="bold" /></button></span> : <Chip selected={selected.length > 0} onClick={() => setOpen(true)} opensDialog={open}>{label}</Chip>;
    $[2] = isRTL;
    $[3] = label;
    $[4] = onToggle;
    $[5] = open;
    $[6] = selected.length;
    $[7] = single;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = group.name;
  let t4;
  if ($[9] !== group.name) {
    t4 = <h2 className="text-base font-semibold text-foreground">{group.name}</h2>;
    $[9] = group.name;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <OverlayCloseButton size="sm" onClick={() => setOpen(false)} />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4) {
    t6 = <div className="flex items-center justify-between border-b border-border px-4 py-3">{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== group.options || $[15] !== onToggle) {
    let t8;
    if ($[17] !== onToggle) {
      t8 = o_0 => <li key={o_0.slug}><label className="flex w-full cursor-pointer items-center gap-2.5 py-3 text-sm text-foreground"><input type="checkbox" checked={o_0.selected} onChange={() => onToggle(o_0.slug)} className="peer sr-only" /><span aria-hidden="true" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 ${o_0.selected ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`}>{o_0.selected && <Check size={10} weight="bold" />}</span><span className="flex-1 truncate">{o_0.name}</span></label></li>;
      $[17] = onToggle;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = group.options.map(t8);
    $[14] = group.options;
    $[15] = onToggle;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <ul className="flex-1 overflow-y-auto px-4 py-2">{t7}</ul>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== group.name || $[22] !== open || $[23] !== t6 || $[24] !== t8) {
    t9 = <Drawer isOpen={open} onOpenChange={setOpen} placement="bottom" size="md" aria-label={t3}>{t6}{t8}</Drawer>;
    $[21] = group.name;
    $[22] = open;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t2 || $[27] !== t9) {
    t10 = <>{t2}{t9}</>;
    $[26] = t2;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function _temp2(o) {
  return o.selected;
}